<template>
  <div class="chart-container">
    <canvas ref="canvas"></canvas>
  </div>
</template>

<script>
import { Doughnut } from 'vue-chartjs';

function generateColors(count) {
  const baseColors = [
    '#007bff',
    '#ff6600',
    '#ffcc00',
    '#cc0066',
    '#00cccc',
    '#66cc66',
    '#cc3300',
    '#ff3366',
    '#9900cc',
    '#999999',
  ];
  const uniqueColors = new Set(baseColors);
  while (uniqueColors.size < count) {
    uniqueColors.add(`#${Math.floor(Math.random() * 16777215).toString(16)}`);
  }
  return Array.from(uniqueColors).slice(0, count);
}

export default {
  extends: Doughnut,
  name: 'StatusMessageDoughnutChart',
  props: {
    messages: {
      type: Array,
      required: true,
    },
    chartOptions: {
      type: Object,
      default: () => ({
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          position: 'right',
          labels: {
            usePointStyle: true,
            pointStyle: 'circle',
            fontFamily:
              '-apple-system,system-ui,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif',
            boxWidth: 10,
            padding: 12,
          },
        },
        cutoutPercentage: 70,
      }),
    },
  },
  data() {
    return {
      internalChartData: null,
      internalChartOptions: null,
    };
  },
  methods: {
    updateChart() {
      const counts = {};

      this.messages.forEach(entry => {
        const status = entry.status_message || this.$t('STATUS.UNKNOWN');
        counts[status] = (counts[status] || 0) + 1;
      });

      const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);
      const labels = sorted.map(([label]) => label);
      const data = sorted.map(([, value]) => value);
      const backgroundColor = generateColors(labels.length);

      if (data.length === 0) {
        this.renderChart({ labels: [], datasets: [] }, { responsive: true });
        return;
      }

      this.internalChartData = {
        labels,
        datasets: [
          {
            data,
            backgroundColor,
            borderWidth: 1,
          },
        ],
      };

      this.internalChartOptions = {
        ...this.chartOptions,
        animation: {
          onComplete: () => {
            const chartInstance = this.$data._chart;
            if (!chartInstance) return;

            const ctx = chartInstance.ctx;
            ctx.font = 'bold 14px sans-serif';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';

            const meta = chartInstance.getDatasetMeta(0);
            const dataset = chartInstance.data.datasets[0].data;
            const total = dataset.reduce((sum, val) => sum + val, 0);

            const minPercentageToShow = 5;

            meta.data.forEach((element, index) => {
              const percentageValue = (dataset[index] / total) * 100;
              if (percentageValue < minPercentageToShow) return;

              const percentage = `${percentageValue.toFixed(1)}%`;
              const pos = element.tooltipPosition();

              ctx.fillStyle = '#000';
              ctx.fillText(percentage, pos.x, pos.y);
            });
          },
        },
      };

      this.renderChart(this.internalChartData, this.internalChartOptions);
    },
  },
  watch: {
    messages: {
      immediate: true,
      handler() {
        if (this.messages && this.messages.length > 0) {
          this.updateChart();
        }
      },
    },
  },
  mounted() {
    this.updateChart();
  },
};
</script>

<style scoped>
.chart-container {
  position: relative;
  height: 240px;
  display: flex;
  align-items: center;
  justify-content: center;
}

::v-deep .chartjs-legend ul {
  padding: 0 !important;
  margin: 0 !important;
}
</style>
